import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import WeekDetail from './WeekDetail';
import '../styles/holy-shipped.css';

interface WeekSummary {
  weekNumber: number;
  weekOf: string;
  title: string;
  description: string;
  completionRate: number;
  featuresShipped: number;
  keyHighlights: string[];
  status: 'complete' | 'in-progress' | 'upcoming';
}

const HolyShipped: React.FC = () => {
  const params = useParams<{ weekNumber?: string }>();
  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);

  useEffect(() => {
    if (params.weekNumber) {
      const weekNum = parseInt(params.weekNumber);
      if (!isNaN(weekNum)) {
        setSelectedWeek(weekNum);
      }
    }
  }, [params.weekNumber]);

  const weeks: WeekSummary[] = [
    {
      weekNumber: 1,
      weekOf: "August 3, 2025",
      title: "Infrastructure Foundation & Review Intelligence",
      description: "Built enterprise-grade AWS infrastructure while shipping production-ready review intelligence system for both brands.",
      completionRate: 100,
      featuresShipped: 14,
      keyHighlights: [
        "Both Mars Men & Hollow Socks fully operational",
        "Review AI processing live customer feedback",
        "Enterprise infrastructure with brand isolation",
        "Single sign-on across all tools"
      ],
      status: 'complete'
    },
    {
      weekNumber: 2,
      weekOf: "August 10, 2025", 
      title: "Review System at Scale & Business Intelligence",
      description: "Deployed complete review intelligence system processing 11,831 reviews extracting 1,989 marketing highlights. Built churn analytics, inventory automation, and multiple business integrations.",
      completionRate: 100,
      featuresShipped: 11,
      keyHighlights: [
        "11,831 reviews processed: 1,989 marketing highlights extracted",
        "2,266 customer transformation stories identified",
        "Live churn & LTV analytics dashboard for Mars Men",
        "Inventory automation + video generation systems deployed"
      ],
      status: 'complete'
    },
    {
      weekNumber: 3,
      weekOf: "August 17, 2025",
      title: "Production Infrastructure & Advanced Analytics",
      description: "Deployed 13 production systems including delivery issue prevention platform with dashboard, LLM intelligence engine, comprehensive churn analytics, and centralized VEO3 billing. Built the technical foundation for proactive customer experience management.",
      completionRate: 100,
      featuresShipped: 13,
      keyHighlights: [
        "Delivery prevention platform: 550+ daily packages with LLM intelligence",
        "238,000+ orders analyzed for churn analytics surpassing Stay AI",
        "VEO3 billing consolidated: One account instead of dozens",
        "Slack-integrated alerts for inventory & forecast automation"
      ],
      status: 'complete'
    },
    {
      weekNumber: 4,
      weekOf: "August 24, 2025",
      title: "Mission Control & Data Infrastructure Overhaul",
      description: "Launched Mission Control dashboard for shipping exception monitoring, rebuilt entire Marsmen data lake architecture, and delivered multiple automation tools. Major focus on ETL optimization and customer experience enhancements.",
      completionRate: 100,
      featuresShipped: 11,
      keyHighlights: [
        "Mission Control Dashboard: Production-ready exception monitoring",
        "Complete Shopify data pipeline rebuild into Marsmen data lake",
        "Buy 3 Get 3 remediation tool with automated value calculations",
        "Midjourney N8N workflow for Notion-integrated image generation"
      ],
      status: 'complete'
    },
    {
      weekNumber: 5,
      weekOf: "August 31, 2025",
      title: "Package Tracking Infrastructure & CX Automation Foundations",
      description: "Secured enterprise package tracking capabilities, deployed multiple automation tools across teams, completed logistics platform ready for end-to-end testing, and executed critical brand separation with ongoing strategic advisory.",
      completionRate: 100,
      featuresShipped: 8,
      keyHighlights: [
        "Logistics tracking platform: Fully built and ready for end-to-end testing with 50K+ orders/month",
        "TrackingMore API: Negotiated 39% discount ($1,629→$1,000/mo) for enterprise tracking",
        "MidJourney workflow deployed for both Mars Men & Hollow Socks creative automation",
        "Brand separation: Mostly complete, continuing advisory with Mary leveraging past expertise"
      ],
      status: 'complete'
    },
    {
      weekNumber: 6,
      weekOf: "September 9, 2025",
      title: "Review Systems Fix & Strategic Pivot to Creative/Partnership Automation",
      description: "Fixed critical workflows and launched fraud detection while initiating strategic shift based on Ben's feedback. Scheduled with Hannah for partnership automation planning and prioritizing creative-forward automations with Brennan. This pivot positions us for scalable Q4 growth through automated creative production and partnership management.",
      completionRate: 100,
      featuresShipped: 13,
      keyHighlights: [
        "Strategic pivot: Creative & partnership automations now priority based on Ben's feedback",
        "Nano Banana interface: Custom AI image editor for creative team",
        "Amazon fraud detection: Monitoring 1000+ ASINs with daily alerts",
        "Brennan collaboration: Prioritizing creative-forward automation pipeline"
      ],
      status: 'complete'
    },
    {
      weekNumber: 7,
      weekOf: "September 16, 2025",
      title: "Creative Automation Super Week & Control Center Launch",
      description: "Rolled out Gemini-powered creative tooling, automated Frame.io QC, AI review search, Shopify monitoring, and SES email infrastructure while launching the Easy Street control center.",
      completionRate: 87,
      featuresShipped: 9,
      keyHighlights: [
        "Gemini creative studio delivers text-driven image editing for the team",
        "Custom creative interface launched for multi-asset prompt orchestration",
        "AI review search (Bedrock + Claude) live for creative cherry-picking",
        "SES email infrastructure ready across both brands"
      ],
      status: 'complete'
    }
  ];

  if (selectedWeek !== null) {
    return (
      <WeekDetail 
        weekNumber={selectedWeek} 
        onBack={() => {
          setSelectedWeek(null);
          navigate('/holy-shipped');
        }} 
      />
    );
  }

  return (
    <div className="dashboard">
      <Header />

      {/* Stats Header */}
      <div className="hs-stats-header">
        <div className="dashboard-container">
          <div className="hs-stats-grid">
            <div className="hs-stat-card">
              <span className="hs-stat-value">{weeks.filter(w => w.status === 'complete').length}</span>
              <span className="hs-stat-label">Weeks Complete</span>
            </div>
            <div className="hs-stat-card">
              <span className="hs-stat-value">{weeks.reduce((sum, w) => sum + w.featuresShipped, 0)}</span>
              <span className="hs-stat-label">Total Features</span>
            </div>
            <div className="hs-stat-card">
              <span className="hs-stat-value">2</span>
              <span className="hs-stat-label">Brands Live</span>
            </div>
          </div>
        </div>
      </div>

      <main className="dashboard-main">
        <div className="dashboard-container">
          <div className="hs-overview-header">
            <h2 className="hs-overview-title">Weekly Progress Overview</h2>
            <p className="hs-overview-subtitle">
              Track our velocity building Easy Street's automation infrastructure and tools. 
              Click any week to see detailed deliverables and technical breakdown.
            </p>
          </div>

          {/* Week Cards */}
          <div className="hs-weeks-grid">
            {weeks.map((week) => (
              <div 
                key={week.weekNumber}
                className={`hs-week-card ${week.status}`}
                onClick={() => week.status !== 'upcoming' && navigate(`/holy-shipped/week/${week.weekNumber}`)}
              >
                <div className="hs-week-card-header">
                  <div className="hs-week-card-left">
                    <h3 className="hs-week-card-number">Week {week.weekNumber}</h3>
                    <p className="hs-week-card-date">{week.weekOf}</p>
                  </div>
                  <div className="hs-week-card-right">
                    <div className={`hs-week-status-badge ${week.status}`}>
                      {week.status === 'complete' ? '✅ Complete' : 
                       week.status === 'in-progress' ? '🚧 In Progress' : 
                       '📅 Upcoming'}
                    </div>
                  </div>
                </div>

                <div className="hs-week-card-content">
                  <h4 className="hs-week-card-title">{week.title}</h4>
                  <p className="hs-week-card-description">{week.description}</p>

                  <div className="hs-week-card-metrics">
                    <div className="hs-week-metric">
                      <span className="hs-week-metric-value">{week.featuresShipped}</span>
                      <span className="hs-week-metric-label">Features</span>
                    </div>
                    <div className="hs-week-metric">
                      <span className="hs-week-metric-value">{week.completionRate}%</span>
                      <span className="hs-week-metric-label">Complete</span>
                    </div>
                    {week.status === 'complete' && (
                      <div className="hs-week-metric">
                        <span className="hs-week-metric-value">📊</span>
                        <span className="hs-week-metric-label">View Details</span>
                      </div>
                    )}
                  </div>

                  <div className="hs-week-highlights">
                    <h5 className="hs-highlights-title">Key Highlights:</h5>
                    <ul className="hs-highlights-list">
                      {week.keyHighlights.map((highlight, index) => (
                        <li key={index} className="hs-highlight-item">{highlight}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {week.status === 'complete' && (
                  <div className="hs-week-card-footer">
                    <span className="hs-view-details-text">Click to view detailed report →</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>© 2024 Easy Street Brands. Building the future of e-commerce automation.</p>
          <div className="footer-links">
            <a href="https://easystreetbrands.com" target="_blank" rel="noopener noreferrer">Easy Street</a>
            <span className="separator">•</span>
            <a href="https://mengotomars.com" target="_blank" rel="noopener noreferrer">Mars Men</a>
            <span className="separator">•</span>
            <a href="https://hollowsocks.com" target="_blank" rel="noopener noreferrer">Hollow Socks</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HolyShipped;
